/**
 * The enemy update: sensing, behaviour dispatch, material reactions, death.
 *
 * The rule this file exists to enforce is docs/03 §6's last line: *no enemy may
 * opt out of the simulation*. Every tick, every active enemy is asked what the
 * cells it is standing in are doing to it — acid corrodes it, charge in a
 * puddle shocks it, cold makes it brittle, heat sets it alight — with the
 * answers derived from its composition rather than from a per-enemy flag. A
 * boss gets exactly the same treatment as a soot gremlin; that is the point.
 *
 * Death is the other half of the ecology. A body does not vanish: it writes
 * cells back into the world (`corpseMaterial`, `corpseDebris`, `deathBehavior`),
 * so a fungal beast's corpse keeps spreading, a molten miner's corpse keeps
 * burning, and grave soil plus the blood of whatever died on it stands back up.
 *
 * Budgeting: at most N enemies are stepped per tick with a round-robin cursor,
 * and skipped enemies get a proportionally larger `dt` when their turn comes
 * so behaviour speed stays population-independent.
 */

import {
  BodyFlag,
  Comp,
  DamageType,
  Team,
  World,
  type DamageTypeId,
} from '../ecs';
import {
  Death,
  Status,
  bodyDensity,
  conductivity,
  corpseDebris,
  corpseMaterial,
  corrosionResist,
  deathBehavior,
  digPower,
  equilibriumTemp,
  freezePoint,
  ignitionPoint,
  isOrganic,
  sanctity,
  susceptibilityProfile,
  type EnemyDef,
  type EnemyRegistry,
} from '../enemyData';
import { Category, Flag, M, Tag, VOID, type MatId } from '../../sim/materials';
import { MAT } from '../../sim/materialData';
import type { CellWorld } from '../../sim/world';
import type { Rng } from '../../core/rng';
import { clamp, lerp } from '../../core/math';
import { MOVE, ProbeBudget, lineOfSight, type NavCaps } from './pathing';
import { stepBody, type StepLimits } from '../body';
import {
  applyStatus,
  hasStatus,
  initBlackboard,
  runBehavior,
  statusStacks,
  type AiFrame,
  type CellBudget,
  type EnemyAiCtx,
} from './behaviors';
import { applyAmbientStatus, tickCombatTimers, tickStatuses } from '../../combat/status';
import { compositionEffectiveness } from '../../combat/resolve';

export type { EnemyAiCtx, AiFrame } from './behaviors';
export {
  AiState,
  applyStatus,
  clearStatus,
  hasStatus,
  initBlackboard,
  statusStacks,
} from './behaviors';
export { tickCombatTimers, tickStatuses } from '../../combat/status';

// ------------------------------------------------------------- derived cache

/**
 * What the material simulation needs to know about a body in order to hurt it.
 *
 * Split out of `DefCache` because the player has no `EnemyDef` and every one of
 * these is derivable from the ECS composition arrays. Nothing in
 * `applyMaterialReactions` may reach for a def: the whole point of docs/03 §6
 * is that acid works on a thing because of what the thing is made of, and the
 * player is made of materials like everything else.
 */
export interface BodyProfile {
  conductivity: number;
  corrosionResist: number;
  ignitionPoint: number;
  freezePoint: number;
  organic: boolean;
  sanctity: number;
  equilibriumTemp: number;
  voidBodied: boolean;
  /** 12 damage-type multipliers, indexed by DamageType. */
  susceptibility: Float32Array;
}

/**
 * Per-def scalars that would otherwise be recomputed for every enemy every
 * tick. Built lazily on first sight of a def and never invalidated — material
 * definitions are frozen after `finalizeMaterials()`, so these cannot drift.
 */
interface DefCache extends BodyProfile {
  caps: NavCaps;
  density: number;
  corpseMat: MatId;
  debrisMat: MatId;
  shatterMat: MatId;
  death: number;
}

const defCache = new Map<string, DefCache>();

export function cacheFor(def: EnemyDef): DefCache {
  let c = defCache.get(def.id);
  if (c !== undefined) return c;

  const corpse = corpseMaterial(def);
  const debris = corpseDebris(def, false);
  const shattered = corpseDebris(def, true);

  let voidBodied = false;
  for (const e of def.composition) {
    if ((M.tags[MAT[e.mat]] & Tag.VOID) !== 0) voidBodied = true;
  }

  c = {
    caps: {
      jumpSpeed: def.jumpSpeed,
      stepUp: Math.max(1, Math.round(def.halfH * 0.35)),
      maxDrop: def.jumpSpeed > 0 ? 64 : 12,
      digPower: digPower(def),
      runSpeed: def.speed,
      halfW: def.halfW,
      halfH: def.halfH,
    },
    conductivity: conductivity(def),
    corrosionResist: corrosionResist(def),
    ignitionPoint: ignitionPoint(def),
    freezePoint: freezePoint(def),
    organic: isOrganic(def),
    sanctity: sanctity(def),
    density: bodyDensity(def),
    equilibriumTemp: equilibriumTemp(def),
    corpseMat: MAT[corpse],
    debrisMat: debris === null ? VOID : MAT[debris],
    shatterMat: shattered === null ? VOID : MAT[shattered],
    death: deathBehavior(def),
    susceptibility: susceptibilityProfile(def, new Float32Array(12)),
    voidBodied,
  };
  defCache.set(def.id, c);
  return c;
}

/** Damage multiplier for a hit. The number combat should be asking for. */
export function damageMultiplier(def: EnemyDef, type: DamageTypeId, w?: World, i?: number): number {
  return profileMultiplier(cacheFor(def), type, w, i);
}

function profileMultiplier(
  prof: BodyProfile,
  type: DamageTypeId,
  w?: World,
  i?: number,
): number {
  let mult = prof.susceptibility[type];
  if (w !== undefined && i !== undefined) {
    // §5.1: a frozen body has brittleness driven to 0.9 — physical impact is
    // how you cash that in. Wet bodies conduct at full strength.
    if (type === DamageType.PHYSICAL && hasStatus(w, i, Status.FROZEN)) mult *= 1.9;
    if (type === DamageType.SHOCK && hasStatus(w, i, Status.WET)) mult *= 1.6;
    if (type === DamageType.FIRE && hasStatus(w, i, Status.WET)) mult *= 0.55;
  }
  return mult;
}

// ------------------------------------------------- composition-derived profile

/**
 * A `BodyProfile` for an entity that has no `EnemyDef` — in practice the
 * player, who is created with `TRANSFORM | BODY | SPRITE | HEALTH | PLAYER` and
 * so appears in no AI query. That omission meant `applyMaterialReactions` never
 * ran for the player and `tickStatuses` was called only for `enemyBuf`: acid,
 * lava, toxin, void, charge, burning, freezing and every damage-over-time did
 * literally nothing to them. The only write to player HP in the entire codebase
 * was self-risk in `resolve.ts`, so `onPlayerDeath` was unreachable.
 *
 * Rebuilt in place each tick rather than cached: it is one entity, four
 * materials, and a cached profile would go stale the moment an archetype or a
 * transformation changed what the player is made of.
 */
const looseProfile: BodyProfile = {
  conductivity: 0, corrosionResist: 0.3, ignitionPoint: Infinity,
  freezePoint: -Infinity, organic: false, sanctity: 0, equilibriumTemp: 20,
  voidBodied: false, susceptibility: new Float32Array(12),
};

/** Weight the core material carries over the rest, mirroring enemyData. */
const CORE_SHARE = 0.35;

export function profileOf(w: World, i: number, out: BodyProfile = looseProfile): BodyProfile {
  const c = i * 4;
  const core = w.coreMat[i];
  let total = 0;
  let cond = 0;
  let corr = 0;
  let sanct = 0;
  let equil = 0;
  let organic = 0;
  let ignition = Infinity;
  let freeze = -Infinity;
  let voidBodied = false;

  for (let k = 0; k < 4; k++) {
    const frac = w.compFrac[c + k];
    if (frac <= 0) continue;
    const m = w.compMat[c + k];
    const weight = frac * (m === core ? 1 + CORE_SHARE : 1);
    total += weight;
    cond += weight * M.electricConductivity[m];
    corr += weight * M.corrosionResist[m];
    sanct += weight * M.sanctity[m];
    equil += weight * M.spawnTemp[m];
    const tg = M.tags[m];
    if ((tg & (Tag.ORGANIC | Tag.BIOMASS | Tag.LIVING)) !== 0) organic += weight;
    if ((tg & Tag.VOID) !== 0) voidBodied = true;
    const ign = M.ignitionTemp[m];
    if (ign < 32000 && ign < ignition) ignition = ign;
    const frz = M.freezesAt[m];
    if (frz > -273 && frz > freeze) freeze = frz;
    if ((tg & (Tag.ORGANIC | Tag.LIVING)) !== 0 && freeze < -5) freeze = -5;
  }

  if (total <= 0) {
    // No composition authored. Treat it as inert rather than as immune: an
    // entity that opts out of the material sim is the one thing §6 forbids.
    out.conductivity = 0.1;
    out.corrosionResist = 0.3;
    out.ignitionPoint = Infinity;
    out.freezePoint = -Infinity;
    out.organic = false;
    out.sanctity = 0;
    out.equilibriumTemp = 20;
    out.voidBodied = false;
    out.susceptibility.fill(1);
    return out;
  }

  out.conductivity = cond / total;
  out.corrosionResist = corr / total;
  out.sanctity = sanct / total;
  out.equilibriumTemp = equil / total;
  out.organic = organic / total >= 0.3;
  out.ignitionPoint = ignition;
  out.freezePoint = freeze;
  out.voidBodied = voidBodied;
  for (let d = 0; d < 12; d++) {
    out.susceptibility[d] = compositionEffectiveness(w, i, d as DamageTypeId);
  }
  return out;
}

// ------------------------------------------------------------------ spawning

/**
 * Write a def onto an entity slot: body, health, composition, AI. Spawners call
 * this so the packed `compMat`/`compFrac`/`coreMat` arrays — which everything
 * downstream reads for reactions — are populated in exactly one place.
 */
export function configureEnemy(w: World, i: number, def: EnemyDef, registry: EnemyRegistry): void {
  w.mask[i] |=
    Comp.TRANSFORM | Comp.BODY | Comp.HEALTH | Comp.ENEMY | Comp.AI | Comp.STATUS | Comp.COMPOSITION;

  w.halfW[i] = def.halfW;
  w.halfH[i] = def.halfH;
  w.bodyFlags[i] = def.bodyFlags;
  w.gravityScale[i] = (def.bodyFlags & BodyFlag.NO_GRAVITY) !== 0 ? 0 : 1;

  const c = cacheFor(def);
  // Mass from real density and real volume: a bone titan is heavy because bone
  // is dense and it is big, not because someone typed 12 into a mass field.
  w.mass[i] = (c.density / 1000) * (def.halfW * 2) * (def.halfH * 2) * 0.01;

  w.hp[i] = w.maxHp[i] = def.hp;
  w.poise[i] = w.maxPoise[i] = def.poise;
  w.team[i] = Team.ENEMY;

  w.coreMat[i] = MAT[def.coreMat];
  const base = i * 4;
  for (let k = 0; k < 4; k++) {
    const entry = def.composition[k];
    w.compMat[base + k] = entry === undefined ? VOID : MAT[entry.mat];
    w.compFrac[base + k] = entry === undefined ? 0 : entry.fraction;
  }

  w.aiKind[i] = registry.kindOf(def.id);
  initBlackboard(w, i);
}

// ------------------------------------------------------------------- system

const enemyBuf = new Int32Array(1024);
const chainBuf = new Int32Array(256);
const probes = new ProbeBudget(48);
const cellBudget: CellBudget = { left: 0 };

/** Reused across every enemy, every tick. The only AiFrame that ever exists. */
const frame: AiFrame = {
  w: null as unknown as World,
  cells: null as unknown as CellWorld,
  rng: null as unknown as Rng,
  dt: 0,
  now: 0,
  i: 0,
  def: null as unknown as EnemyDef,
  caps: null as unknown as NavCaps,
  target: -1,
  tx: 0,
  ty: 0,
  dx: 0,
  dy: 0,
  dist: 0,
  los: false,
  probes,
  cellBudget,
  ctx: null as unknown as EnemyAiCtx,
};

/**
 * Round-robin cursor for over-budget frames. Module scope because there is one
 * enemy population; `resetEnemyAi()` clears it for tests and level transitions.
 */
let rrCursor = 0;
let simTime = 0;

export function resetEnemyAi(): void {
  rrCursor = 0;
  simTime = 0;
}

/** Telemetry for the F2 debug panel — read-only snapshot, no allocation. */
export const enemyAiStats = {
  live: 0,
  stepped: 0,
  cellsWritten: 0,
  probesSpent: 0,
  /** Bodies integrated this tick. Unlike `stepped`, this is never round-robined. */
  integrated: 0,
};

// -------------------------------------------------------------- body integration

/** Enemy bodies matching this mask steer in 2D; `behaviors.ts` `isFlyer` agrees. */
const FLIER_MASK = BodyFlag.NO_GRAVITY | BodyFlag.FLOATS;

const bodyBuf = new Int32Array(1024);
/** One scratch object for the whole pass — a per-enemy literal is not affordable. */
const bodyLimits: StepLimits = { stepUp: 1, cornerCorrection: MOVE.cornerCorrection, minGapCells: 10 };

/**
 * Integrate every enemy body against the grid. **Call this once per sim tick,
 * before `updateEnemies`.**
 *
 * Until #72 this pass did not exist. `stepBody` is the game's one integrator
 * and its only caller was `stepMover`, which only the player has — so the AI
 * wrote `w.vx`/`w.vy` for every enemy every tick and nothing turned that into a
 * position. `navigateTo`, the structural-graph pathing, the flier and swimmer
 * steering, the CHARGER family's approach and #49's lunge sweep were all
 * computing correct velocities that were discarded on the next line. It is
 * also why `w.grounded` was permanently 0 for enemies: `stepBody` is the only
 * thing that writes it, so `behaviors.ts`'s `if (nav.jump && w.grounded[i] ===
 * 1)` could never fire and no enemy could jump.
 *
 * **The ordering is load-bearing.** `sweepLunge` resolves a dash over the
 * segment `[x − v·dt, x]` — behind the body, never ahead of it. That trailing
 * segment is only honest if this tick's velocity has *already* moved the body,
 * so integration has to happen before the behaviour that reads it. Run the
 * other way round, the sweep claims ground the body has not crossed yet, which
 * is the exact defect #49 was fixed to remove.
 *
 * Two deliberate departures from `stepMover`, both so this agrees with code
 * that already exists rather than inventing a second movement model:
 *
 *   - **Gravity comes from `MOVE`**, the same package `pathing.ts` integrates
 *     jump arcs against. `jumpRange` splits the arc into a rise half and a fall
 *     half and uses no apex hang; adding one here would make the navigator's
 *     "can it clear that gap?" answer wrong in the body's favour.
 *   - **Fliers are exempt.** The mask is `behaviors.ts`'s own `isFlyer`
 *     (NO_GRAVITY | FLOATS), not NO_GRAVITY alone. `coolant_serpent` carries
 *     FLOATS without NO_GRAVITY and is steered by `flyStep`, so gating on
 *     NO_GRAVITY would have gravity fighting the navigator's vertical intent
 *     on the one def in the roster where the two disagree. Buoyancy scales that
 *     gravity term, so it is exempt on exactly the same mask.
 *   - **Buoyancy reuses `stepBody`'s submersion reading** rather than taking
 *     one. `sampleSubmersion` is the dominant cost in this pass and a second
 *     scan would be the most expensive line in it; the value from the previous
 *     integration is the same one-substep-old reading `applyVertical` uses for
 *     the player. See the gravity block below and #74.
 *
 * Hitstop freezes the body, exactly as it freezes the player's in
 * `stepMovement` — a hitstop that does not stop the thing being hit is not
 * hitstop. Everything else is integrated every tick: `updateEnemies` runs its
 * behaviours round-robin, but a body whose *turn* was skipped still has a
 * velocity, and applying it at a stride would make travel speed depend on
 * population.
 */
export function stepEnemyBodies(
  w: World,
  cells: CellWorld,
  dt: number,
  registry: EnemyRegistry,
): void {
  const n = w.query(Comp.ENEMY | Comp.BODY | Comp.TRANSFORM, bodyBuf);
  enemyAiStats.integrated = 0;

  for (let k = 0; k < n; k++) {
    const i = bodyBuf[k];
    if (w.hp[i] <= 0) continue;
    if (w.hitstop[i] > 0) continue;

    const def = registry.byKind(w.aiKind[i]);
    if (def === undefined) continue;

    const cache = cacheFor(def);

    if ((w.bodyFlags[i] & FLIER_MASK) === 0) {
      let vy = w.vy[i];
      // Asymmetric, and the asymmetry is the navigator's: rise is cheap so a
      // jump reaches the height `canJumpUp` promised, fall is heavy so the
      // landing arrives when `jumpRange` says it does.
      let g = (vy < 0 ? MOVE.riseGravity : MOVE.fallGravity) * w.gravityScale[i];

      // Buoyancy, from what the body is made of — #74. Until this existed the
      // density model was the player's alone, so every non-flier sank in water
      // whatever its composition; measured, all 49 of them sank at *the same
      // rate*, to the last float, and the only thing that changed the number
      // was the fluid's drag. That is the one place composition-derived
      // behaviour stopped at the player, and it flattened flooding a room into
      // a uniform drowning.
      //
      // **The submersion reading is shared, not taken.** `sampleSubmersion` is
      // the dominant term in this pass — it scans the full body AABB, 1536
      // cells for a boss — and adding a second scan here would double the most
      // expensive thing the pass does. `stepBody` already wrote `submersion`
      // and `submergedMat` for this body at the end of its last integration,
      // which is exactly the reading `stepMover` hands `applyVertical` for the
      // player: one substep old, from the same writer, and free.
      const sub = w.submersion[i];
      if (sub > 0.02) {
        const lm = w.submergedMat[i];
        if (lm !== VOID) {
          // docs/02 §2.7's model — `gravity *= lerp(1, buoyancy(material), s)`,
          // continuous in density, so mercury throws a body up and oil barely
          // slows it. The body's side of the ratio is `cacheFor().density`, the
          // composition-weighted figure `swimStep` already steers on and
          // `configureEnemy` already derives mass from, so nothing new is
          // authored and nothing new is loaded.
          //
          // No `fluidSinkBias`. That term is a *feel* knob in the player's
          // `MovementTuning`, live-editable from the debug panel and worth
          // 0.22 of a gravity so a swimming player still settles; charging it
          // to an enemy would mean authoring a tuning number here for bodies
          // whose only input is their material list. Without it the rule is
          // exactly the physical one `swimStep` states: denser than the fluid
          // sinks, lighter rises.
          const ratio = M.density[lm] / cache.density;
          g *= lerp(1, clamp(1 - ratio, -1.2, 1), sub);
        }
      }

      vy += g * dt;
      if (vy > MOVE.maxFallSpeed) vy = MOVE.maxFallSpeed;
      w.vy[i] = vy;
    }

    // `caps.stepUp` is what `groundFollow` assumed when it decided this body
    // could walk over that rubble without jumping. Passing anything else makes
    // the pathfinder lie.
    bodyLimits.stepUp = cache.caps.stepUp;
    stepBody(w, i, cells, dt, bodyLimits);
    enemyAiStats.integrated++;
  }
}

/**
 * The entry point. Call once per sim tick.
 *
 * `playerIdx` is the entity index of the player (or -1 when there is none —
 * enemies then idle and leash home rather than crashing, which matters during
 * worldgen previews and the debug room).
 */
export function updateEnemies(
  w: World,
  cells: CellWorld,
  playerIdx: number,
  dt: number,
  rng: Rng,
  ctx: EnemyAiCtx,
): void {
  simTime += dt;

  const n = w.query(Comp.ENEMY | Comp.AI | Comp.TRANSFORM, enemyBuf);
  enemyAiStats.live = n;
  enemyAiStats.stepped = 0;
  enemyAiStats.probesSpent = 0;

  const cellCap = ctx.maxCellWritesPerTick ?? 1400;
  cellBudget.left = cellCap;
  const probeLimit = ctx.maxProbesPerEntity ?? 48;

  const hasPlayer = playerIdx >= 0 && playerIdx < w.capacity && w.alive[playerIdx] === 1;

  // Iframes, hitstop and stagger are written by the hit path and were drained
  // by nobody: an enemy that took one melee hit was invulnerable for the rest
  // of the run. This is the one place that drains them, for every entity.
  tickCombatTimers(w, dt);

  // Statuses decay for everyone, every tick — everyone meaning every entity
  // carrying `Comp.STATUS`, not every entity that happens to have an AI.
  // Burning that ticks at a rate determined by how many enemies are alive would
  // be a bug the player experiences as "fire is unreliable"; burning that skips
  // the player entirely is one they experience as "fire is decoration".
  tickStatuses(w, cells, dt, rng, (i, amount, type) => {
    if (ctx.damageEntity) ctx.damageEntity(i, amount, type, -1);
    else w.hp[i] -= amount;
  });

  frame.w = w;
  frame.cells = cells;
  frame.rng = rng;
  frame.now = simTime;
  frame.ctx = ctx;

  // The player is not in `enemyBuf` and never will be, so their contact with
  // the world is resolved explicitly rather than as a side effect of the AI
  // loop. Same function, same derivation, no exemption (docs/03 §6).
  if (hasPlayer && w.hp[playerIdx] > 0) {
    frame.i = playerIdx;
    frame.dt = dt;
    frame.target = -1;
    applyMaterialReactions(frame, profileOf(w, playerIdx));
  }

  if (n === 0) return;

  const maxSteps = ctx.maxEnemiesPerTick ?? 32;
  const steps = n <= maxSteps ? n : maxSteps;
  // Skipped enemies are compensated with a proportionally larger dt, so a
  // 200-enemy room runs each enemy slower but not *differently*.
  const stride = n <= maxSteps ? 1 : Math.ceil(n / maxSteps);
  const stepDt = dt * stride;

  frame.dt = stepDt;

  const px = hasPlayer ? w.x[playerIdx] : 0;
  const py = hasPlayer ? w.y[playerIdx] : 0;

  for (let k = 0; k < steps; k++) {
    const i = enemyBuf[(rrCursor + k) % n];
    if (w.alive[i] !== 1) continue;

    const def = ctx.registry.byKind(w.aiKind[i]);
    if (def === undefined) continue;
    const cache = cacheFor(def);

    if (w.hp[i] <= 0) {
      killEnemy(w, cells, i, def, cache, rng, ctx);
      continue;
    }

    // Hitstop is the combat workstream's; the AI just respects it.
    if (w.hitstop[i] > 0) {
      w.hitstop[i] = Math.max(0, w.hitstop[i] - stepDt);
      continue;
    }

    enemyAiStats.stepped++;
    probes.reset(probeLimit);

    frame.i = i;
    frame.def = def;
    frame.caps = cache.caps;

    if (hasPlayer && w.hp[playerIdx] > 0) {
      frame.target = playerIdx;
      frame.tx = px;
      frame.ty = py;
      frame.dx = px - w.x[i];
      frame.dy = py - w.y[i];
      frame.dist = Math.hypot(frame.dx, frame.dy);
      // Only pay for a raycast when the answer could change behaviour.
      frame.los =
        frame.dist <= def.aggroRange * 1.6 &&
        lineOfSight(cells, w.x[i], w.y[i], px, py, 1);
    } else {
      frame.target = -1;
      frame.tx = w.aiHome[i * 2];
      frame.ty = w.aiHome[i * 2 + 1];
      frame.dx = 0;
      frame.dy = 0;
      frame.dist = Infinity;
      frame.los = false;
    }

    applyMaterialReactions(frame, cache);
    if (w.hp[i] <= 0) {
      killEnemy(w, cells, i, def, cache, rng, ctx);
      continue;
    }

    runBehavior(frame);

    // The body is a weapon whatever the behaviour is doing. `contactDamage` is
    // authored on all 54 defs and had no reader, so the safest place in the
    // room was the inside of the thing trying to kill you. It resolves here,
    // once per stepped enemy, rather than inside a family: an idling, fleeing
    // or leashing enemy is still solid.
    //
    // Round-robin applies — an enemy skipped this tick applies no contact this
    // tick — which is the same compensation `stepDt` makes for everything else
    // in this loop.
    ctx.contactHit?.(i, def);

    enemyAiStats.probesSpent += probeLimit - probes.left;
  }

  rrCursor = (rrCursor + steps) % n;
  enemyAiStats.cellsWritten = cellCap - cellBudget.left;
}

// ------------------------------------------------------- material reactions

/**
 * How much of the body a sample stands for.
 *
 * THE CONTACT MODEL, stated once so nobody has to infer it from the call site
 * (issue #53). A body is billed for the world it is standing in by taking
 * exactly TWO samples — the body centre and the cell under the feet — never
 * once per cell of its silhouette. That is deliberate and it is the whole
 * reason an authored `toxicity` means something:
 *
 *   - **Immersion is not a function of size.** A gas fills the volume the body
 *     occupies, so an 11x23 player and a 41x81 colossus are equally *in* it.
 *     Billed per overlapping cell they would cover 242 and 3280 cells of the
 *     same cloud, and the colossus would take 13x the damage for no stated
 *     reason — every gas in the game balanced by accident of body height.
 *   - **Gases and liquids use the SAME model**, so `toxicity: 0.25` means one
 *     thing and not two. The feet sample is weighted lower than the body
 *     sample because standing ankle-deep in a pool is less contact than being
 *     under it; that partial-immersion reading is the only asymmetry, it
 *     applies to gas and liquid alike, and it is these two constants.
 *   - **A material is billed at most once per tick.** The feet sample is
 *     skipped outright when it reads the same material as the body, so full
 *     immersion costs `IMMERSION_BODY`, never `IMMERSION_BODY + IMMERSION_FEET`.
 *
 * Anything that wants a size-dependent hazard has to say so somewhere a
 * designer can read, not get it for free out of a loop bound.
 */
export const IMMERSION_BODY = 1;
export const IMMERSION_FEET = 0.6;

/**
 * Contact damage per second at `corrosiveness = 1` / `toxicity = 1`, against a
 * body with no resistance and a susceptibility of 1.
 *
 * Exported because these are the numbers that turn an authored 0..1 material
 * property into HP, and a test that transcribes them instead of importing them
 * is testing its own copy. The authored values were set against these; #43
 * owns re-deriving them and is blocked on #50, so they do not move here.
 */
export const CONTACT_ACID_DPS = 30;
export const CONTACT_TOXIC_DPS = 14;

/**
 * What the world is doing to this body right now. Six cell reads and a handful
 * of comparisons — cheap enough that no enemy needs an exemption, which is
 * precisely why no enemy gets one.
 */
function applyMaterialReactions(f: AiFrame, cache: BodyProfile): void {
  const { w, i, cells, dt } = f;
  const cx = Math.floor(w.x[i]);
  const cy = Math.floor(w.y[i]);
  const feetY = Math.floor(w.y[i] + w.halfH[i] - 1);

  const bodyMat = cells.peek(cx, cy);
  const feetMat = cells.peek(cx, feetY);
  const bodyTemp = cells.getTemp(cx, cy);
  const bodyFlags = cells.getFlags(cx, cy);
  const feetFlags = cells.getFlags(cx, feetY);

  // Cache what the body is standing in for anyone downstream (movement reads
  // ground friction from it; the HUD reads it for the "why am I taking damage"
  // readout).
  w.submergedMat[i] = bodyMat;
  w.groundMat[i] = feetMat;

  // Two samples, weighted by immersion — see IMMERSION_BODY above for why this
  // is not a loop over the body's cells. Each returns the seconds of *ignition*
  // exposure that sample is worth; everything else it charges, it charges
  // itself.
  const bodyBurn = reactToCell(f, cache, bodyMat, bodyFlags, dt, IMMERSION_BODY);
  const feetBurn = feetMat !== bodyMat
    ? reactToCell(f, cache, feetMat, feetFlags, dt, IMMERSION_FEET)
    : 0;

  // --- thermal: ignition and freezing are properties of the body, not flags.
  //
  // Both are conditions the body is *in*, sampled once a frame, so both charge
  // an exposure meter rather than adding a stack per call — see
  // `applyAmbientStatus` and docs/15 §5.2. Crossing the threshold still ignites
  // you *instantly*: any contact at all puts a body on the first rung, which is
  // the part of "ignition is an event" that is true. What is not an event is
  // being *maximally engulfed*, and that is the only thing that now takes time.
  //
  // A BODY HAS ONE IGNITION METER AND IT IS BILLED ONCE A TICK. That is
  // IMMERSION_BODY's rule ("a material is billed at most once per tick") applied
  // to the status channel, and it has to be stated here because there are three
  // ways to be on fire — the body's cell is flagged BURNING, the feet's cell is,
  // the temperature is over the ignition point — and in a cell of FIRE all three
  // are true at once. Charged separately they compound: measured, real fire
  // reached BURNING x5 in 0.683 s against the 1.983 s a burning cell alone took,
  // which is the per-frame defect rebuilt out of two correct call sites.
  const hot = Math.max(bodyTemp, cells.getTemp(cx, feetY));
  const overIgnition = cache.ignitionPoint < Infinity && hot >= cache.ignitionPoint;
  const flagBurn = Math.max(bodyBurn, feetBurn);
  if (overIgnition || flagBurn > 0) {
    // The authored full-exposure durations stay where they were: 3.0 s for
    // standing in something hot enough to light you, 2.5 s for standing in
    // something already alight. Where both are true the deeper one wins, which
    // is the same "billed once" rule stated for the charge.
    const charge = overIgnition ? Math.max(dt, flagBurn) : flagBurn;
    applyAmbientStatus(w, i, Status.BURNING, charge, overIgnition ? 3.0 : 2.5, 6);
  }
  if (cache.freezePoint > -Infinity && hot <= cache.freezePoint - 8) {
    // A self-heating body (molten iron freezes at 1400 °C) is not solidified by
    // standing in 20 °C air — it has to be *quenched*. So a hot body only
    // seizes when it is sitting in something with the heat capacity to carry
    // that heat away, which is exactly docs/03 §6's "water flash-solidifies it".
    const selfHeating = cache.equilibriumTemp > 200;
    const quenched =
      bodyMat !== VOID &&
      (M.category[bodyMat] === Category.LIQUID || M.category[bodyMat] === Category.SLUDGE) &&
      M.heatCapacity[bodyMat] >= 2;
    if (!selfHeating || quenched) {
      // FROZEN carries no damage-over-time, so time-to-death is the wrong
      // instrument for it and this ladder is a *brittleness* ladder:
      // `brittleMul` runs 1.35x to 2.05x across it, `damageTakenMul` 1.22x to
      // 1.66x, and `shatterFrozen`'s threshold falls from 0.29 to 0.19 of max
      // HP. Climbing it in three ticks meant a body that touched cold for one
      // sixtieth of a second was exactly as brittle as one that stood in it,
      // and 0.100 s of cold at a 30 Hz step was worth 0.025 s of it at 120 Hz.
      applyAmbientStatus(w, i, Status.FROZEN, dt, 1.4, 3);
      // The slow is a *rate*, not a per-tick multiply.
      //
      // This line is the *player's* control loss. `moveSpeedMul` used to look
      // like the slow and was read by nothing; #119 measured what wiring it
      // would do and deleted it instead — see the note where it used to live in
      // `src/combat/status.ts`. An enemy's control loss is not this line at
      // all: `sharedTransitions` in `behaviors.ts` sees FROZEN and returns
      // before the body drives, and #119 converted that drag to a rate too.
      //
      // As a bare `*= 0.6` this one compounded at the tick rate:
      // measured, a body holding a run covered 0.667 of its clean-air distance
      // at a 30 Hz step, 0.367 at 60 Hz and 0.209 at 120 Hz — a drag whose
      // strength was the step size. `0.6 ** (dt * 60)` is that same drag
      // authored per second, and the exponent is exactly 1 at the 60 Hz the
      // shipping loop steps this pass at, so the authored 0.6 does not move and
      // the 60 Hz column does not either: 0.367 before, 0.367 after.
      w.vx[i] *= Math.pow(0.6, dt * 60);
    }
  }

  // --- burning: take damage, and spread. A burning enemy running through the
  // room is one of the best emergent hazards in the game, so it is deliberate.
  if (hasStatus(w, i, Status.BURNING)) {
    const stacks = statusStacks(w, i, Status.BURNING);
    hurt(f, cache, 5.5 * stacks * dt, DamageType.FIRE, VOID);
    if (cellBudget.left > 0 && f.rng.chance(0.5)) spreadFire(f, cx, feetY);
  }

  // --- charge: a conductive body standing in a charged (or merely wet)
  // conductor is the water/electricity rule from §5.1, applied to a corpse-to-be.
  const charged = (bodyFlags & Flag.CHARGED) !== 0 || (feetFlags & Flag.CHARGED) !== 0;
  if (charged && cache.conductivity > 0.1) {
    hurt(f, cache, 22 * cache.conductivity * dt, DamageType.SHOCK, bodyMat);
    applyStatus(w, i, Status.SHOCKED, 0.6, 1, 4);
    chainShock(f, cache);
  }

  // --- void erases, radiance burns the unholy. Both are one-liners because the
  // materials already carry the semantics.
  if (bodyMat !== VOID) {
    const tags = M.tags[bodyMat];
    if ((tags & Tag.VOID) !== 0 && !cache.voidBodied) {
      hurt(f, cache, 16 * dt, DamageType.VOID, bodyMat);
    }
    if (M.sanctity[bodyMat] > 0.5 && cache.sanctity < -0.1) {
      hurt(f, cache, 18 * dt, DamageType.RADIANT, bodyMat);
    }
  }
}

/**
 * Corrosion, toxicity and wetness from ONE SAMPLE of contact.
 *
 * `scale` is an immersion weight, not a cell count: the caller takes two
 * samples and passes `IMMERSION_BODY` / `IMMERSION_FEET`. Nothing may call this
 * once per overlapping cell — see the note above `IMMERSION_BODY`.
 *
 * THE OTHER HALF OF THE CONTACT MODEL, and the one #53 did not reach: this
 * function runs once per frame, so everything it charges has to be a *rate*.
 * The damage always was (`... * dt`). The status applications were not — each
 * added a whole stack per call, which put POISONED at its cap after five frames
 * of contact and made the affliction a function of frame rate rather than of
 * exposure. `applyAmbientStatus` is the dt-scaled form; see its note in
 * `src/combat/status.ts`, and `tests/ambientAffliction.test.ts` for the bands.
 *
 * The thermal channel now charges the same way (#110) — the numbers are in
 * docs/15 §5.2. Ignition is the one affliction this function does *not* apply:
 * it returns the exposure the sample is worth and lets `applyMaterialReactions`
 * bill the body's single ignition meter once, because two samples and a
 * temperature reading are three descriptions of one condition and charging all
 * three rebuilds the per-frame defect out of correct parts.
 *
 * @returns seconds of ignition exposure this sample is worth, 0 if none.
 */
function reactToCell(
  f: AiFrame,
  cache: BodyProfile,
  mat: MatId,
  flags: number,
  dt: number,
  scale: number,
): number {
  if (mat === VOID) return 0;
  const { w, i } = f;
  const tags = M.tags[mat];

  const corr = M.corrosiveness[mat];
  if (corr > 0) {
    // §5.1: CORROSIVE × ORGANIC is fast; ceramic is immune. Both fall out of
    // the composition-derived resistance, so acid "just works" on new enemies.
    const taken = corr * CONTACT_ACID_DPS * (1 - cache.corrosionResist) * scale * dt;
    if (taken > 0.001) {
      hurt(f, cache, taken, DamageType.ACID, mat);
      applyAmbientStatus(w, i, Status.CORRODING, dt * scale, 1.2, 5);
    }
  }

  const tox = M.toxicity[mat];
  if (tox > 0 && cache.organic) {
    hurt(f, cache, tox * CONTACT_TOXIC_DPS * scale * dt, DamageType.TOXIC, mat);
    applyAmbientStatus(w, i, Status.POISONED, dt * scale, 2.0, 5);
  }

  const cat = M.category[mat];
  if (cat === Category.LIQUID && (tags & Tag.CONDUCTIVE) !== 0) {
    applyStatus(w, i, Status.WET, 4.0, 1, 1);
  }

  if ((tags & Tag.FUNGAL) !== 0 && cache.organic) {
    applyAmbientStatus(w, i, Status.INFECTED, dt * scale, 3.0, 4);
  }

  if ((flags & Flag.BURNING) !== 0 && cache.ignitionPoint < Infinity) return dt * scale;
  return 0;
}

/** A burning body lights what it brushes past. Budgeted like every cell write. */
function spreadFire(f: AiFrame, cx: number, feetY: number): void {
  const { cells } = f;
  for (let k = 0; k < 4 && cellBudget.left > 0; k++) {
    const x = cx + f.rng.int(-3, 3);
    const y = feetY + f.rng.int(-2, 1);
    const m = cells.peek(x, y);
    if (m === VOID) continue;
    const ign = M.ignitionTemp[m];
    if (ign >= 32000) continue;
    cells.setTemp(x, y, ign + 40);
    cells.addFlag(x, y, Flag.BURNING);
    cellBudget.left--;
  }
}

/**
 * Charge jumps between conductive bodies. Distance-limited and one hop deep —
 * a full graph traversal would be both slower and less readable than "the two
 * metal things standing in the puddle both light up".
 */
function chainShock(f: AiFrame, cache: BodyProfile): void {
  const { w, i } = f;
  if (cache.conductivity < 0.3) return;
  const n = w.query(Comp.ENEMY | Comp.TRANSFORM, chainBuf);
  let hops = 0;
  for (let k = 0; k < n && hops < 3; k++) {
    const j = chainBuf[k];
    if (j === i || w.hp[j] <= 0) continue;
    const dx = w.x[i] - w.x[j];
    const dy = w.y[i] - w.y[j];
    if (dx * dx + dy * dy > 48 * 48) continue;
    const other = f.ctx.registry.byKind(w.aiKind[j]);
    if (other === undefined) continue;
    const oc = cacheFor(other);
    if (oc.conductivity < 0.15) continue;
    hops++;
    const amount = 10 * oc.conductivity * f.dt;
    if (f.ctx.damageEntity) f.ctx.damageEntity(j, amount, DamageType.SHOCK, i);
    else w.hp[j] -= amount;
    applyStatus(w, j, Status.SHOCKED, 0.5, 1, 4);
  }
}

/**
 * Environmental damage goes through the same door as everything else.
 *
 * `srcMat` rides along so the death line can name the material rather than
 * saying "killed by the world (physical)". docs/06 §4 is explicit that the
 * difference between a player blaming the game and blaming themselves is
 * almost entirely whether they can see why they died, and "you stood in lava"
 * is a very different sentence from "you died".
 */
function hurt(
  f: AiFrame,
  prof: BodyProfile,
  amount: number,
  type: DamageTypeId,
  srcMat: MatId = VOID,
): void {
  if (amount <= 0) return;
  const { w, i } = f;
  const final = amount * profileMultiplier(prof, type, w, i);
  if (f.ctx.damageEntity) f.ctx.damageEntity(i, final, type, -1, srcMat);
  else w.hp[i] -= final;
}

// --------------------------------------------------------------------- death

/**
 * The body goes back into the world. This is the whole reason corpses are
 * derived from composition: it means the ecology closes. Kill enough fungal
 * beasts in a room and the room becomes fungal; kill a molten miner over a
 * water channel and you get obsidian terrain you can stand on.
 */
function killEnemy(
  w: World,
  cells: CellWorld,
  i: number,
  def: EnemyDef,
  cache: DefCache,
  rng: Rng,
  ctx: EnemyAiCtx,
): void {
  const x = Math.floor(w.x[i]);
  const y = Math.floor(w.y[i]);
  const flags = cache.death;
  const shattered = hasStatus(w, i, Status.FROZEN) || (flags & Death.SHATTER) !== 0;

  // Corpse volume scales with the body, capped so a boss does not paint the
  // room. 220 cells is roughly a 15×15 splatter.
  const volume = Math.min(220, Math.round(def.halfW * def.halfH * 0.8));
  const spread = Math.max(3, Math.round(Math.sqrt(volume)));

  spawnBlob(cells, rng, x, y, spread, cache.corpseMat, volume);

  if ((flags & Death.DEBRIS) !== 0 && cache.debrisMat !== VOID) {
    const mat = shattered ? cache.shatterMat : cache.debrisMat;
    spawnBlob(cells, rng, x, y, spread + 2, mat, Math.round(volume * 0.4));
  }

  // §5.2: burning fungus spores harder — and dying fungus spores regardless.
  if ((flags & Death.SPORE_BURST) !== 0) {
    spawnBlob(cells, rng, x, y - 2, spread + 4, MAT.SPORE_CLOUD, Math.round(volume * 0.6));
  }
  if ((flags & Death.TOXIC_CLOUD) !== 0) {
    spawnBlob(cells, rng, x, y - 2, spread + 3, MAT.TOXIC_SMOKE, Math.round(volume * 0.5));
  }
  if ((flags & Death.IGNITE) !== 0) {
    cells.heatCircle(x, y, spread + 2, 500);
    spawnBlob(cells, rng, x, y - 1, spread, MAT.FIRE, Math.round(volume * 0.3));
  }
  if ((flags & Death.SOLIDIFY) !== 0) {
    // A molten body cools where it lands: real, standable terrain.
    spawnBlob(cells, rng, x, y + 1, spread, MAT.OBSIDIAN, Math.round(volume * 0.35));
  }
  if ((flags & Death.NANITE_BLOOM) !== 0) {
    spawnBlob(cells, rng, x, y, spread + 1, MAT.NANITE_SWARM, Math.round(volume * 0.5));
  }
  if ((flags & Death.RADIANT_FLASH) !== 0) {
    spawnBlob(cells, rng, x, y, spread + 2, MAT.RADIANT_MATTER, Math.round(volume * 0.25));
    cells.heatCircle(x, y, spread + 3, 120);
  }
  if ((flags & Death.VOID_COLLAPSE) !== 0) {
    // Erases rather than adds — a void body leaves a hole in the room.
    cells.damageCircle(x, y, spread + 1, 6);
    spawnBlob(cells, rng, x, y, spread, MAT.VOID_MATTER, Math.round(volume * 0.2));
  }
  if ((flags & Death.DETONATE) !== 0) {
    cells.damageCircle(x, y, spread + 4, 4);
    cells.heatCircle(x, y, spread + 6, 380);
  }
  if ((flags & Death.RAISE) !== 0) {
    // §5.2: GRAVE_SOIL + BLOOD spawns a revenant. The corpse supplies both.
    spawnBlob(cells, rng, x, y + 1, spread, MAT.GRAVE_SOIL, Math.round(volume * 0.3));
    if (ctx.spawnEnemy && rng.chance(0.25)) {
      ctx.spawnEnemy('spectral_revenant', w.x[i], w.y[i], i);
    }
  }

  ctx.onDeath?.(i, def);
  if (ctx.destroyOnDeath !== false) w.destroy(w.handleAt(i));
}

/**
 * Scatter `count` cells of `mat` in a rough disc. Writes only into space the
 * material could actually occupy, so a corpse never replaces a wall.
 */
function spawnBlob(
  cells: CellWorld,
  rng: Rng,
  cx: number,
  cy: number,
  radius: number,
  mat: MatId,
  count: number,
): void {
  if (mat === VOID || count <= 0) return;
  const density = M.density[mat];
  let written = 0;
  const attempts = count * 4;
  for (let k = 0; k < attempts && written < count; k++) {
    const a = rng.float() * Math.PI * 2;
    const d = Math.sqrt(rng.float()) * radius;
    const x = cx + Math.round(Math.cos(a) * d);
    const y = cy + Math.round(Math.sin(a) * d);
    const m = cells.peek(x, y);
    if (m !== VOID) {
      if (M.solid[m] === 1) continue;
      if (M.density[m] > density) continue;
    }
    cells.set(x, y, mat);
    written++;
  }
}

/**
 * Force a death now — for the combat workstream, which owns the kill decision
 * but should not have to know how a body becomes cells.
 */
export function killEnemyAt(
  w: World,
  cells: CellWorld,
  i: number,
  rng: Rng,
  ctx: EnemyAiCtx,
): void {
  const def = ctx.registry.byKind(w.aiKind[i]);
  if (def === undefined) return;
  killEnemy(w, cells, i, def, cacheFor(def), rng, ctx);
}
